"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { HocuspocusProvider } from "@hocuspocus/provider"
import { IndexeddbPersistence } from "y-indexeddb"
import * as Y from "yjs"
import type { SaveStatus, CollaborationContextValue } from "./types"
import { createAttentionMonitor } from "./attention"
import { recordEvent } from "@workspace/observability/browser"

export type { SaveStatus, CollaborationContextValue }

const CollaborationContext = createContext<CollaborationContextValue | null>(null)

export function useCollaboration() {
  const ctx = useContext(CollaborationContext)
  if (!ctx) throw new Error("useCollaboration must be used within CollaborationProvider")
  return ctx
}

/**
 * Safe hook that returns collaboration context if available, or null if outside the provider.
 * Use this in components that may render without collaboration.
 */
export function useCollaborationMaybe(): CollaborationContextValue | null {
  return useContext(CollaborationContext)
}

interface CollaborationProviderProps {
  /** The document name sent to hocuspocus (e.g. "doc-123" or "api-builder:proj-456") */
  documentName: string
  /** WebSocket URL of the hocuspocus server */
  wsUrl: string
  /** Auth API base URL for fetching collab tokens (default: https://api.auth.l.gaz.codes) */
  authApiUrl?: string
  /** Save status debounce in ms — should exceed the server's debounce (default: 4000) */
  saveDebounce?: number
  /**
   * Report focus and input to the server, for platform presence (tdd-0079).
   *
   * Off by default: it sends a stateless message roughly once a minute per open
   * document, which is only worth paying for in an app whose collab server
   * forwards presence on to notifications-api. Apps that have wired that opt in.
   */
  reportAttention?: boolean
  /**
   * Persist the Y.Doc to IndexedDB so the document is available offline and
   * survives reloads (default: true). Set false only for short-lived ephemeral
   * documents where a local cache would be wasteful or undesirable.
   */
  enableOfflinePersistence?: boolean
  /**
   * IndexedDB store name. Defaults to `${documentName}` — apps with multiple
   * Y.Docs sharing a name across surfaces can pass an explicit key to avoid
   * collisions.
   */
  offlineCacheKey?: string
  /**
   * Cache schema version. Bump when the app changes the Y.Doc shape in a way
   * that makes existing cached state incompatible — old caches with a lower
   * version are still queryable for garbage collection, but the active store
   * is namespaced under this version. (default: 1)
   */
  cacheVersion?: number
  /**
   * Called when the server confirms a durable store of the document. Use
   * this to surface a "saved to server at HH:MM:SS" timestamp or trigger
   * dependent work. Fires for every persisted broadcast — typical cadence
   * is once per server debounce window after edits land.
   */
  onPersisted?: (ctx: { storedAt: Date }) => void
  children: ReactNode
}

export function CollaborationProvider({
  documentName,
  wsUrl,
  authApiUrl = process.env.NEXT_PUBLIC_AUTH_API_URL || "https://api.auth.l.gaz.codes",
  saveDebounce = 4000,
  reportAttention = false,
  enableOfflinePersistence = true,
  offlineCacheKey,
  cacheVersion = 1,
  onPersisted,
  children,
}: CollaborationProviderProps) {
  const [connected, setConnected] = useState(false)
  const [synced, setSynced] = useState(false)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("saved")
  const [idbSynced, setIdbSynced] = useState(!enableOfflinePersistence)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>(null)

  const ydoc = useMemo(() => new Y.Doc(), [])

  // IndexedDB persistence — loaded before the network so the document is
  // visible immediately on reload, and edits made while offline are kept.
  // SSR-safe: only attaches in the browser.
  const idbStoreName = useMemo(() => {
    const key = offlineCacheKey ?? documentName
    return `${key}:v${cacheVersion}`
  }, [offlineCacheKey, documentName, cacheVersion])

  useEffect(() => {
    if (!enableOfflinePersistence) return
    if (typeof window === "undefined" || typeof indexedDB === "undefined") return

    const idb = new IndexeddbPersistence(idbStoreName, ydoc)
    idb.on("synced", () => setIdbSynced(true))
    return () => {
      void idb.destroy()
      setIdbSynced(false)
    }
  }, [enableOfflinePersistence, idbStoreName, ydoc])

  // Fetch a fresh collab token. On 401, rotate the auth cookie via
  // /v1/refresh, then retry. On any unrecoverable failure we still resolve
  // (with an empty string) so Hocuspocus' internal `permissionDeniedHandler`
  // doesn't fire — that handler permanently disables retries.
  // The recovery loop is driven by the auth-failed event instead.
  //
  // Concurrent invocations dedupe via tokenInFlightRef: Hocuspocus may call
  // the factory several times in a tight reconnect loop, and we want one
  // refresh attempt, not N.
  const tokenInFlightRef = useRef<Promise<string> | null>(null)

  // One attempt at the collab-token endpoint, with a single 401→refresh→retry.
  // Returns "" on any failure (network, non-OK, non-JSON, missing token).
  const fetchTokenOnce = useCallback(async (): Promise<string> => {
    try {
      let r = await fetch(`${authApiUrl}/v1/collab-token`, {
        credentials: "include",
      })

      if (r.status === 401) {
        const refreshRes = await fetch(`${authApiUrl}/v1/refresh`, {
          method: "POST",
          credentials: "include",
        })
        if (refreshRes.ok) {
          r = await fetch(`${authApiUrl}/v1/collab-token`, {
            credentials: "include",
          })
        }
      }

      if (!r.ok) {
        console.warn(`[Collab] collab-token returned ${r.status}`)
        // Every `return ""` in this function is one step from a document that
        // silently stops syncing (see the note below on why an empty token is
        // fatal). Until now all four were console-only, so the failure was
        // detectable solely by a developer with devtools open at that instant.
        // `status` is low-cardinality; the URL and body are not, and stay in
        // the console line.
        recordEvent(
          "collab.token.failed",
          { reason: "not_ok", status: r.status },
          { error: true },
        )
        return ""
      }
      // The endpoint must return JSON; if a misrouted request yields HTML
      // (e.g. an app shell), parsing throws and we treat it as no token.
      const ct = r.headers.get("content-type") ?? ""
      if (!ct.includes("application/json")) {
        console.warn(`[Collab] collab-token non-JSON response (${ct || "unknown"})`)
        // Almost always a misroute serving an app shell — a deployment or proxy
        // fault, not an auth one, and worth telling apart from the others.
        recordEvent(
          "collab.token.failed",
          { reason: "non_json", content_type: ct || "unknown" },
          { error: true },
        )
        return ""
      }
      const data = (await r.json()) as { token?: string }
      if (!data.token) {
        // A 200 with no token. The most dangerous of the four, because every
        // signal short of the token itself says success.
        recordEvent("collab.token.failed", { reason: "empty_token" }, { error: true })
        return ""
      }
      return data.token
    } catch (err) {
      console.warn("[Collab] fetchToken caught:", err)
      recordEvent("collab.token.failed", { reason: "threw" }, { error: true })
      return ""
    }
  }, [authApiUrl])

  // HocuspocusProvider permanently disables itself (`shouldConnect = false`,
  // "Won't try again") if it ever connects with an EMPTY token — the server
  // closes with the "no token" Unauthorized reason, which is fatal and not
  // recoverable via onAuthenticationFailed. So the token callback must NEVER
  // resolve empty: it retries with capped backoff until it gets a real token.
  // The provider simply stays in "connecting" until auth is ready (e.g. the
  // cookie lands after login / refresh), then connects cleanly — instead of
  // sending "" once and dying for the rest of the session.
  const fetchToken = useCallback(async (): Promise<string> => {
    if (tokenInFlightRef.current) return tokenInFlightRef.current

    const p = (async () => {
      try {
        let attempt = 0
        for (;;) {
          const token = await fetchTokenOnce()
          if (token) return token
          attempt++
          const delayMs = Math.min(15_000, 500 * 2 ** Math.min(attempt - 1, 5))
          await new Promise((res) => setTimeout(res, delayMs))
        }
      } finally {
        tokenInFlightRef.current = null
      }
    })()

    tokenInFlightRef.current = p
    return p
  }, [fetchTokenOnce])

  // Reconnect attempts after auth failure are throttled so a hard-rejected
  // workbook (e.g. permanent ownership mismatch) doesn't busy-loop.
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Latest onPersisted callback in a ref so the provider doesn't re-create
  // when consumers pass an inline arrow function.
  const onPersistedRef = useRef(onPersisted)
  useEffect(() => {
    onPersistedRef.current = onPersisted
  }, [onPersisted])

  // The live provider. Created and destroyed in ONE effect keyed only on the
  // connection-defining inputs (doc name, url, ydoc) — NOT on callbacks.
  //
  // Constructing a HocuspocusProvider opens a WebSocket as a side effect, so it
  // must never live in useMemo: a memo recompute (or a dev StrictMode double
  // render) builds a second provider whose socket isn't paired with the prior
  // one's cleanup, leaving multiple competing sockets on the same doc. That was
  // the cause of "multiple WS, some pending" and ~10s sync lag — outgoing
  // updates went out on one socket while the peer listened on another.
  const [provider, setProvider] = useState<HocuspocusProvider | null>(null)
  const providerRef = useRef<HocuspocusProvider | null>(null)

  useEffect(() => {
    const p = new HocuspocusProvider({
      url: wsUrl,
      name: documentName,
      document: ydoc,
      token: fetchToken,
      onConnect: () => {
        reconnectAttemptsRef.current = 0
        setConnected(true)
        // A reconnect clears a surfaced auth failure — the retry worked, and
        // the debounce/`persisted` path takes the status from here.
        setSaveStatus((prev) => (prev === "failed" ? "saving" : prev))
      },
      onDisconnect: () => setConnected(false),
      onSynced: () => {
        setSynced(true)
        // Only clear an existing "offline" status — don't stomp "saving" (a
        // local edit may be mid-debounce) or "persisted" (server already
        // confirmed durable store). The status will be reconciled by the
        // save-debounce timer and the stateless `persisted` broadcast.
        setSaveStatus((prev) => (prev === "offline" ? "saved" : prev))
      },
      onStateless: ({ payload }) => {
        // Server-originated one-shot messages. Today the only kind is
        // `persisted`, sent after each durable store with a real change.
        try {
          const msg = JSON.parse(payload) as { type?: string; storedAt?: number }
          if (msg.type === "persisted") {
            const storedAt = new Date(msg.storedAt ?? Date.now())
            // Cancel any pending local "saving → saved/offline" debounce —
            // the server's confirmation is more truthful.
            if (saveTimerRef.current) {
              clearTimeout(saveTimerRef.current)
              saveTimerRef.current = null
            }
            setSaveStatus("persisted")
            onPersistedRef.current?.({ storedAt })
          }
        } catch (err) {
          console.warn("[Collab] malformed stateless payload:", err)
        }
      },
      onAuthenticationFailed: ({ reason }) => {
        // Hocuspocus calls disconnect() and stops trying after this fires.
        // Schedule an explicit reconnect with exponential backoff capped at
        // 30s; fetchToken will run again and pick up a refreshed cookie.
        const attempt = ++reconnectAttemptsRef.current
        const delayMs = Math.min(30_000, 500 * 2 ** Math.min(attempt - 1, 6))
        console.warn(
          `[Collab] auth failed: ${reason} — retry #${attempt} in ${delayMs}ms`,
        )
        // `attempt` is the useful half: one failure is a cookie landing late
        // and is unremarkable, while a climbing count is a session that will
        // never recover. Both were indistinguishable from outside.
        recordEvent(
          "collab.auth_failed",
          { attempt, reason: String(reason).slice(0, 64) },
          { error: true },
        )
        // One failure is usually a cookie landing late and resolves on the
        // first retry, so don't alarm the user with it. A second means the
        // session is not coming back on its own — their edits are local-only
        // and they need to know, so surface it.
        if (attempt >= 2) setSaveStatus("failed")
        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = setTimeout(() => {
          void providerRef.current?.connect()
        }, delayMs)
      },
    })

    providerRef.current = p

    /**
     * Attention reporting (tdd-0079 phase 4, client half).
     *
     * Sent over Hocuspocus' stateless channel — the socket is already open, so
     * this needs no new transport. The server forwards it to notifications-api,
     * which is the only thing that can distinguish a user reading this document
     * from one who left it open behind a dozen other tabs.
     */
    const attention = reportAttention
      ? createAttentionMonitor({
          onReport: ({ focused, at }) => {
            try {
              p.sendStateless(JSON.stringify({ type: "attention", focused, at }))
            } catch {
              // The socket may be mid-reconnect. A dropped report costs a
              // notification that should have been suppressed — never a lost
              // one — so it is not worth retrying or surfacing.
            }
          },
        })
      : null
    setProvider(p)

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
      providerRef.current = null
      attention?.stop()
      p.destroy()
    }
    // fetchToken is stable (keyed on authApiUrl); intentionally excluded so a
    // token-callback identity change can never re-open a second socket.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentName, ydoc, wsUrl])

  // Track local changes → "saving" → "saved"/"offline" cycle.
  // The server debounces at 3s; we show "saving" on change and flip to
  // "saved" (or "offline" if the WS is down — IndexedDB still has the data)
  // after saveDebounce ms.
  //
  // `connected` is read via a ref inside the timer callback (not from
  // closure) because the timer is set on edit but the connection state
  // can change between then and when the timer fires. Capturing `connected`
  // in the closure would cause a stale-read flicker: edit while online →
  // timer scheduled with `connected=true` → network drops → timer fires
  // and writes "saved" → derivation effect then corrects to "offline".
  const connectedRef = useRef(connected)
  useEffect(() => {
    connectedRef.current = connected
  }, [connected])

  useEffect(() => {
    const onUpdate = (_update: Uint8Array, origin: unknown) => {
      // Only react to local edits, not remote syncs
      if (origin === provider) return
      setSaveStatus("saving")
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      saveTimerRef.current = setTimeout(() => {
        setSaveStatus(connectedRef.current ? "saved" : "offline")
      }, saveDebounce)
    }

    ydoc.on("update", onUpdate)
    return () => {
      ydoc.off("update", onUpdate)
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [ydoc, provider, saveDebounce])

  // When the connection drops without an active edit in flight, surface
  // "offline" so the UI is honest. Only do this once IndexedDB has hydrated
  // — otherwise a freshly-mounted offline tab flashes "saved" → "offline".
  useEffect(() => {
    if (!enableOfflinePersistence) return
    if (!idbSynced) return
    if (connected) return
    if (saveStatus === "saving") return
    // "failed" is more specific than "offline" — the socket is down because the
    // server rejected us, not because the network went away. Keep it.
    if (saveStatus === "failed") return
    setSaveStatus("offline")
  }, [enableOfflinePersistence, idbSynced, connected, saveStatus])

  // Provider teardown is owned by the creation effect above. The Y.Doc lives
  // for the component's whole lifetime (useMemo []), so destroy it only on
  // unmount.
  useEffect(() => {
    return () => {
      ydoc.destroy()
    }
  }, [ydoc])

  const value = useMemo(
    () => ({ provider, ydoc, connected, synced, saveStatus }),
    [provider, ydoc, connected, synced, saveStatus],
  )

  return (
    <CollaborationContext.Provider value={value}>
      {children}
    </CollaborationContext.Provider>
  )
}
